import { ConfigProvider, DatePicker, Form, type FormInstance, Radio } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import locale from 'antd/es/locale/en_GB';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import { useUserProfile } from '../../fetchHooks/useUserProfile.ts';
import { getEditorFontClassName } from '../../utils/fontUtils';
import { AddHealthMetricForm } from './AddHealthMetricForm.tsx';
import type { ParsedBloodworkResult } from './BloodworkImportModal';
import { MeasurementsList } from './MeasurementsList';
import { ModalButtons } from './ModalButtons';
import { MyDayVariant } from './MyDayPage.tsx';
import MyRichTextEditor from './MyRichTextEditor.jsx';

interface MydayFormProps {
  form?: FormInstance;
  onFinish: (values: any) => void;
  onCancel: () => void;
  isEdit: boolean;
  myday: MydayType;
  type: string;
  children: React.ReactNode;
}

export const MydayForm = ({
  form: externalForm,
  onFinish,
  onCancel,
  isEdit,
  myday,
  type = 'measurement',
  children,
}: MydayFormProps) => {
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;
  const { t } = useTranslation();
  const { data: userProfile } = useUserProfile();
  const isSimplified = userProfile?.services?.includes('simplified');
  const editorFontClassName = getEditorFontClassName(userProfile?.settings?.preferredFont);

  useEffect(() => {
    form.setFieldsValue({
      type: type,
      date: dayjs(), // Initialize with current date
    });
  }, [form, type]);

  const handleImportMeasurements = (measurements: ParsedBloodworkResult[]) => {
    // Get current measurements from form
    const currentMeasurements = form.getFieldValue('measurements') || [];

    // Convert parsed results to form format
    const newMeasurements = measurements.map((measurement) => ({
      healthmetrictypeId: measurement.matchedHealthMetricType?.id,
      value: measurement.value,
    }));

    // Add new measurements to existing ones
    form.setFieldsValue({
      measurements: [...currentMeasurements, ...newMeasurements],
    });
  };

  return (
    <Form form={form} layout="vertical" preserve={false} onFinish={onFinish}>
      <ModalButtons onOk={form.submit} okText={isEdit ? t('myday.save') : t('myday.create')} onCancel={onCancel} />
      <div>
        <Form.Item name="type">
          <span style={{ fontWeight: 'bold', marginRight: '10px' }}>{t('myday.selectType')}:</span>
          <Radio.Group value={type}>
            {!isSimplified && <Radio.Button value={MyDayVariant.EXPERIENCE}>{t('myday.experience')}</Radio.Button>}
            <Radio.Button value={MyDayVariant.DIARY}>{isSimplified ? 'Hyttebok' : t('myday.diary')}</Radio.Button>
            {!isSimplified && <Radio.Button value={MyDayVariant.MEASUREMENT}>{t('myday.measurement')}</Radio.Button>}
            {!isSimplified && (
              <Radio.Button value={MyDayVariant.HEALTH_RELATED}>{t('myday.healthRelated')}</Radio.Button>
            )}
            <Radio.Button value={MyDayVariant.NOTE}>{t('myday.note')}</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <div>
          <ConfigProvider locale={locale}>
            <Form.Item
              name="date"
              label={<span style={{ fontWeight: 'bold' }}>{t('myday.selectDate')}:</span>}
              rules={[
                {
                  required: true,
                  message: t('myday.pleaseChooseMeetingDateAndTime'),
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <DatePicker format="YYYY-MM-DD" placeholder={t('myday.selectDate')} />
            </Form.Item>
          </ConfigProvider>
        </div>
        <br />
        {!isSimplified && <MeasurementsList mydayId={myday?.id} />}
        {!isSimplified && <AddHealthMetricForm onImportMeasurements={handleImportMeasurements} />}
        <div>
          <Form.Item
            name="description"
            label={t('myday.description')}
            rules={[
              {
                required: true,
                message: t('myday.pleaseWriteDescription'),
              },
            ]}
          >
            <TextArea placeholder={t('myday.pleaseAddTextHere')} rows={1} />
          </Form.Item>
        </div>
        <div>
          <Form.Item
            name="note"
            label={t('myday.note')}
            rules={[
              {
                required: true,
                message: t('myday.pleaseWriteNote'),
              },
            ]}
          >
            <MyRichTextEditor myday={myday} text={myday?.note} editorClassName={editorFontClassName} />
          </Form.Item>
        </div>
        {children}
        <ModalButtons onOk={form.submit} okText={isEdit ? t('myday.save') : t('myday.create')} onCancel={onCancel} />
      </div>
    </Form>
  );
};
